import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, IconButton, LinearProgress, Collapse } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { orange50, orange10, gray80, gray110, white } from '../../../constants/colors';
import { globalHeaderHeight } from '../../../constants/location';
import { useUploadState } from '../../../contexts/UploadContext';
import { uploadTaskStatus, uploadProgressValue, type UploadTask } from '../../../types/upload';

const COMPLETED_DISPLAY_MS = 5000;
// パネルに表示する「進行中」ステータス（calling完了 = アップロード完了とみなす）
const ACTIVE_STATUSES = [
  uploadTaskStatus.uploading,
  uploadTaskStatus.creating,
  uploadTaskStatus.calling,
] as const;

const styles = {
  container: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 1500,
    width: 360,
    maxHeight: 400,
    display: 'flex',
    flexDirection: 'column',
    '@media screen and (max-width: 600px)': {
      bottom: 'auto',
      top: globalHeaderHeight,
      right: 'auto',
      left: 8,
      width: 280,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: orange50,
    borderRadius: '8px 8px 0 0',
    cursor: 'pointer',
  },
  headerCollapsed: {
    borderRadius: '8px',
  },
  headerTitle: {
    color: white,
    fontSize: '14px',
    fontWeight: 700,
  },
  headerIcon: {
    color: white,
    padding: '2px',
  },
  taskList: {
    maxHeight: 300,
    overflowY: 'auto',
    padding: '8px 0',
    backgroundColor: white,
  },
  taskItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 16px',
  },
  taskIcon: {
    marginTop: '2px',
    fontSize: '20px',
  },
  taskContent: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  taskLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: gray110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  taskStatus: {
    fontSize: '12px',
    color: gray80,
    marginTop: '2px',
  },
  taskFileNames: {
    fontSize: '11px',
    color: gray80,
    marginTop: '2px',
  },
  progressBar: {
    marginTop: '6px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: orange10,
    '& .MuiLinearProgress-bar': {
      backgroundColor: orange50,
      borderRadius: '2px',
    },
  },
};

const getStatusText = (task: UploadTask): string => {
  switch (task.status) {
    case uploadTaskStatus.uploading:
      return `アップロード中 ${task.uploadedCount}/${task.fileCount}`;
    case uploadTaskStatus.creating:
      return 'レコード作成中...';
    case uploadTaskStatus.calling:
      return 'API呼び出し中...';
    case uploadTaskStatus.processing:
      return `読取中 ${task.processedCount || 0}/${task.totalResultCount || 0}`;
    case uploadTaskStatus.completed:
      return '完了';
    case uploadTaskStatus.error:
      return task.errorMessage || 'エラー';
    default:
      return '';
  }
};

const getProgress = (task: UploadTask): number => {
  if (task.status === uploadTaskStatus.completed) return uploadProgressValue.completed;
  if (task.status === uploadTaskStatus.error) return 0;
  if (task.status === uploadTaskStatus.uploading && task.fileCount > 0) {
    return (task.uploadedCount / task.fileCount) * uploadProgressValue.uploadingMax;
  }
  if (task.status === uploadTaskStatus.creating) return uploadProgressValue.creating;
  if (task.status === uploadTaskStatus.calling) return uploadProgressValue.calling;
  if (task.status === uploadTaskStatus.processing) {
    const processed = task.processedCount || 0;
    const total = task.totalResultCount || 0;
    const remainingRange = uploadProgressValue.completed - uploadProgressValue.calling;
    return total > 0
      ? uploadProgressValue.calling + (processed / total) * remainingRange
      : uploadProgressValue.calling;
  }
  return 0;
};

// パネルに表示するタスク行（進行中 or 完了済み表示中）
interface DisplayTask {
  task: UploadTask;
  completed: boolean;
}

const UploadProgressPanel: React.FC = () => {
  const { tasks } = useUploadState();
  const [expanded, setExpanded] = useState(true);
  // パネルに表示するタスク行（進行中タスク＋完了済み表示中タスク）
  const [displayTasks, setDisplayTasks] = useState<DisplayTask[]>([]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeTasks = tasks.filter(
    t => !t.hidePanel && (ACTIVE_STATUSES as readonly string[]).includes(t.status),
  );

  const activeTasksRef = useRef(activeTasks);
  activeTasksRef.current = activeTasks;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  useEffect(() => {
    const currentActiveTasks = activeTasksRef.current;
    setDisplayTasks(prev => {
      const next: DisplayTask[] = [...prev];

      // 進行中タスクを追加・更新
      currentActiveTasks.forEach(task => {
        const idx = next.findIndex(d => d.task.id === task.id);
        if (idx >= 0) {
          next[idx] = { task, completed: false };
        } else {
          next.push({ task, completed: false });
        }
      });

      // 進行中だったが今は active でないタスク → 完了済みに変更
      return next.map(d => {
        if (d.completed) return d;
        const stillActive = currentActiveTasks.some(t => t.id === d.task.id);
        if (!stillActive) {
          return { task: d.task, completed: true };
        }
        return d;
      });
    });
  }, [activeTasks.length, tasks]);

  useEffect(() => {
    // 全タスクが完了済みになったら5秒後に閉じる
    if (displayTasks.length > 0 && displayTasks.every(d => d.completed)) {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => {
        setDisplayTasks([]);
        closeTimer.current = null;
      }, COMPLETED_DISPLAY_MS);
    } else if (displayTasks.some(d => !d.completed)) {
      // 進行中タスクがある間はタイマーをキャンセル
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    }
  }, [displayTasks]);

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    };
  }, []);

  if (displayTasks.length === 0) return null;

  const hasActive = displayTasks.some(d => !d.completed);
  const activeCount = displayTasks.filter(d => !d.completed).length;
  const headerText = hasActive ? `アップロード中（${activeCount}件）` : 'アップロード完了';

  return (
    <Paper elevation={8} sx={styles.container}>
      <Box
        sx={[styles.header, !expanded && styles.headerCollapsed]}
        onClick={(): void => setExpanded(prev => !prev)}
      >
        <Typography sx={styles.headerTitle}>{headerText}</Typography>
        <IconButton size="small" sx={styles.headerIcon}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={styles.taskList}>
          {displayTasks.map(({ task, completed }) => (
            <Box key={task.id} sx={styles.taskItem}>
              {completed ? (
                <CheckCircleOutlineIcon sx={{ ...styles.taskIcon, color: orange50 }} />
              ) : (
                <CloudUploadOutlinedIcon sx={{ ...styles.taskIcon, color: orange50 }} />
              )}
              <Box sx={styles.taskContent}>
                <Typography sx={styles.taskLabel}>{task.label}</Typography>
                <Typography sx={styles.taskFileNames}>
                  {task.fileNames.map((name, i) => (
                    <React.Fragment key={i}>
                      {name}
                      {i < task.fileNames.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography sx={styles.taskStatus}>
                  {completed ? 'アップロード完了' : getStatusText(task)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={completed ? uploadProgressValue.completed : getProgress(task)}
                  sx={styles.progressBar}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default UploadProgressPanel;
